import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  inject,
  NgZone,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PrimecustomModule } from 'src/app/shared/primeng.custom.module';
import {
  COMMON_SVG_IMG_PATH,
  GOOGLE_MAPS_SEARCH_URL,
  LOG_CREATE,
} from 'src/app/shared/common.constant';
import { ScrollerModule } from 'primeng/scroller';
import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  RAISED_ALERTS,
  FILTER_ALERT_CATEGORY,
} from 'src/app/shared/api.constant';
import { SsoService } from '../../services/sso.service';
import { Subject, takeUntil, finalize } from 'rxjs';
import { CommonService } from 'src/app/shared/service/common.service';
import { DateTimePipe } from '../../../shared/pipes/date-time.pipe';

import { alertColorMap } from './notification.config';
import { PROFILE, MY_PROFILE, ALERTS } from 'src/app/shared/routeLink.constant';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UserLoggerService } from 'src/app/shared/service/user-logger.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { AlertSseService } from 'src/app/core/services/alert-sse.service';
import { AlertCategoryPipe } from 'src/app/shared/pipes/alert-category.pipe';
import { GoogleMapsService } from 'src/app/shared/service/google-maps-service.service';
import { mapOptions } from 'src/app/features/tracking/map/map.config';

@Component({
  selector: 'app-notification-side-drawer',
  standalone: true,
  imports: [
    PrimecustomModule,
    CommonModule,
    ReactiveFormsModule,
    ScrollerModule,
    ScrollingModule,
    DateTimePipe,
    AlertCategoryPipe,
  ],
  templateUrl: './notification-side-drawer.component.html',
  styleUrl: './notification-side-drawer.component.scss',
})
export class NotificationSideDrawerComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() visible = false;
  @Input() hasNewNotification = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() drawerClosed = new EventEmitter<void>();
  @Output() notificationsViewed = new EventEmitter<void>();
  @ViewChild('notificationScroller')
  notificationScroller?: ElementRef<HTMLDivElement>;

  private readonly unsubscribe$ = new Subject<void>();

  data: any[] = [];

  categoriesForm: FormGroup;

  categoriesOptions: any[] = [];

  totalNotifs = 0;
  unread = 0;
  svgPath = COMMON_SVG_IMG_PATH;
  selectedLabelTemplate = '{0}';
  showNewNotificationBanner = false;

  currentPage = 1;
  pageSize = 10;
  totalPages = 0;
  isFetchingMore = false;
  isLoading = false;
  isSubscribed = true;
  isNotifications = true;
  isPermitteted = true;

  GOOGLE_MAPS_URL: any = GOOGLE_MAPS_SEARCH_URL;

  selectedLocationNotification: any = null;
  mapLoadingNotification: any = null;
  private readonly zone = inject(NgZone);
  private readonly googleMapsService = inject(GoogleMapsService);

  constructor(
    private readonly fb: FormBuilder,
    private readonly ssoService: SsoService,
    private readonly commonService: CommonService,
    private readonly router: Router,
    private readonly userlogService: UserLoggerService,
    private readonly titleService: Title,
    public readonly loadingService: LoadingService,
    private readonly alertSseService: AlertSseService
  ) {
    this.categoriesForm = this.fb.group({
      categoriesOptions: [[]],
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']?.currentValue === true) {
      this.onDrawerOpen();
      return;
    }

    if (
      changes['hasNewNotification']?.currentValue === true &&
      !changes['hasNewNotification']?.firstChange &&
      this.visible
    ) {
      this.showNewNotificationBanner = true;
    }
  }

  private onDrawerOpen() {
    if (this.hasNewNotification) {
      this.notificationsViewed.emit();
    }

    if (this.categoriesOptions.length === 0) {
      this.fetchAlertCategories();
    }

    if (this.hasNewNotification || this.data.length === 0) {
      this.currentPage = 1;
      this.fetchNotifications();
    } else {
      this.currentPage = Math.ceil(this.data.length / this.pageSize);
    }
  }

  ngOnInit() {
    this.categoriesForm
      .get('categoriesOptions')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((selected) => {
        this.updateLabelTemplate(selected);
        this.currentPage = 1;
        this.fetchNotifications();
      });

    this.alertSseService.notificationStatus$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((status) => {
        this.isPermitteted = status.hasPermission;
        this.isSubscribed = status.isSubscribed;
      });

    this.alertSseService.alerts$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event) => {
        if ((event === true || event?.newAlert === true) && this.visible) {
          this.showNewNotificationBanner = true;
        }
      });
  }

  onScrollDown() {
    if (
      this.isFetchingMore ||
      (this.totalPages > 0 && this.currentPage >= this.totalPages)
    ) {
      return;
    }

    this.currentPage++;
    this.fetchNotifications();
  }

  onNewNotificationClick() {
    this.showNewNotificationBanner = false;
    this.currentPage = 1;
    this.fetchNotifications();

    if (this.notificationScroller?.nativeElement) {
      this.notificationScroller.nativeElement.scrollTop = 0;
    }
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 700;
    const position = element.scrollTop + element.clientHeight;
    const height = element.scrollHeight;

    if (position > height - threshold) {
      this.onScrollDown();
    }
  }

  fetchAlertCategories() {
    const payload = {
      vehicleTypeCode: this.commonService.getVehicleTypeCode(),
      type: 'Alerts',
    };

    this.ssoService
      .getSharedURL(`${FILTER_ALERT_CATEGORY}`, payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => {
          if (res?.status) {
            this.categoriesOptions = (res.data?.response ?? []).map(
              (item: any) => ({
                label: item.alertCategoryName,
                value: item.alertCategoryCode,
              })
            );

            if (!this.categoriesForm.get('categoriesOptions')?.value) {
              this.categoriesForm.patchValue(
                {
                  categoriesOptions: [],
                },
                { emitEvent: false }
              );
            }

            this.selectedLabelTemplate = '{0}';
          }
        },
      });
  }

  fetchNotifications() {
    const selectedCategories =
      this.categoriesForm.get('categoriesOptions')?.value ?? [];

    const isFirstPage = this.currentPage === 1;
    if (isFirstPage) {
      this.isLoading = true;
    } else {
      this.isFetchingMore = true;
    }

    const payload: any = {
      vehicleTypeCode: this.commonService.getVehicleTypeCode(),
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
    };

    if (selectedCategories.length > 0) {
      payload.alertCategoryCode = selectedCategories.join(',');
    }

    this.ssoService
      .getVehicleURL(`${RAISED_ALERTS}`, payload)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.isLoading = false;
          this.isFetchingMore = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res?.status && res?.data?.response?.alerts) {
            const alerts = res.data.response.alerts ?? [];

            this.data =
              this.currentPage === 1 ? alerts : [...this.data, ...alerts];

            this.totalPages = res.totalPages ?? 0;
            this.totalNotifs = res.data.response.totalAlerts;
            this.unread = res.data.response.unReadAlerts;

            this.isNotifications = this.data.length > 0;
          } else if (this.currentPage === 1) {
            this.data = [];
            this.totalNotifs = 0;
            this.unread = 0;
            this.isNotifications = false;
          }
        },
      });
  }

  updateLabelTemplate(selected: string[]) {
    if (!selected || selected.length === 0) {
      this.selectedLabelTemplate = '{0}';
    } else if (selected.length === this.categoriesOptions.length) {
      this.selectedLabelTemplate = 'All';
    } else {
      this.selectedLabelTemplate = '{0}';
    }
  }

  navigateSubscribeNotifications() {
    this.userlogService.logAction(this.titleService.getTitle(), LOG_CREATE);
    this.router.navigate([`/${ALERTS}/subscribe-notifications`]);
    this.onClose();
  }
  navigateContactUs() {
    this.userlogService.logAction(this.titleService.getTitle(), LOG_CREATE);
    this.router.navigate([`/${PROFILE}/${MY_PROFILE}/contact-us`]);
    this.onClose();
  }

  getAlertColor(code: string): string {
    return alertColorMap[code] ?? '#999';
  }

  getAlertBg(code: string): string {
    const color = alertColorMap[code] ?? '#999';
    return `${color}1F`;
  }

  get showNoPermission(): boolean {
    return !this.isPermitteted;
  }

  get showNotSubscribed(): boolean {
    return this.isPermitteted && !this.isSubscribed;
  }

  get showNoNotifications(): boolean {
    return this.isPermitteted && this.isSubscribed && !this.isNotifications;
  }

  get showNotifications(): boolean {
    return this.isPermitteted && this.isSubscribed && this.isNotifications;
  }
  get showFilterBar(): boolean {
    return (
      this.isPermitteted &&
      this.isSubscribed &&
      (this.isLoading || this.totalNotifs > 0 || this.hasActiveFilter)
    );
  }

  get showFilters(): boolean {
    return (
      this.isPermitteted &&
      this.isSubscribed &&
      (this.totalNotifs > 0 || this.hasActiveFilter)
    );
  }

  get hasActiveFilter(): boolean {
    const selected = this.categoriesForm.get('categoriesOptions')?.value ?? [];
    return selected.length > 0;
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.drawerClosed.emit();
    this.showNewNotificationBanner = false;
    this.selectedLocationNotification = null;
    this.mapLoadingNotification = null;
    this.categoriesForm.patchValue(
      {
        categoriesOptions: [],
      },
      { emitEvent: false }
    );
    this.selectedLabelTemplate = '{0}';
  }

  toggleLocationPanel(notification: any, index: number): void {
    if (this.selectedLocationNotification === notification) {
      this.selectedLocationNotification = null;
    } else {
      this.selectedLocationNotification = notification;
      this.loadLocationMap(notification, index);
    }
  }

  loadLocationMap(notification: any, index: number): void {
    const position = {
      lat: Number(notification.coordinates?.latitude),
      lng: Number(notification.coordinates?.longitude),
    };

    if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) {
      this.mapLoadingNotification = null;
      return;
    }

    this.mapLoadingNotification = notification;
    this.zone.runOutsideAngular(() => {
      this.googleMapsService.loadAPI().then(
        () => this.initializeMap(notification, index, position),
        () => this.clearMapLoading()
      );
    });
  }

  private initializeMap(notification: any, index: number, position: any): void {
    requestAnimationFrame(() => {
      const mapElement = document.getElementById(`location-map-${index}`);
      if (mapElement && this.selectedLocationNotification === notification) {
        this.renderMap(mapElement, position);
      }
      this.zone.run(() => {
        this.mapLoadingNotification = null;
      });
    });
  }

  private renderMap(mapElement: HTMLElement, position: any): void {
    const mapOpts: any = {
      ...mapOptions,
      center: position,
      zoom: 15,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
    };

    const map = new google.maps.Map(mapElement, mapOpts);
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: map,
      position,
    });
    marker.title = `Alert location at ${position.lat}, ${position.lng}`;
  }

  private clearMapLoading(): void {
    this.zone.run(() => {
      this.mapLoadingNotification = null;
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
